import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { ScrollText, Copy, X, Check } from "lucide-react";

interface PMSLog {
  Time: string;
  LogLevel: string;
  Connector: string;
  ConnectorHotelId: string;
  HotelCode: string;
  SaasId: string;
  Ref: string;
  Method: string;
  Action: string;
  SizeBytes: string;
  Datapoints: string;
  ResponseTime: string;
  TraceFile: string;
  StatusCode: string;
  Status: string;
}

/** Trace API response (GET /v1/tracelog/trace/:requestId) */
interface TraceEntry {
  timestamp: string;
  level: string;
  message: string;
  meta?: Record<string, unknown>;
}

interface TraceResponse {
  requestId: string;
  userId?: string;
  connector?: string;
  hotelId?: string;
  method?: string;
  fileName?: string;
  filePath?: string;
  fileSize?: number;
  dateFolder?: string;
  entries: TraceEntry[];
  totalEntries: number;
  metadata?: {
    firstTimestamp?: string;
    lastTimestamp?: string;
    duration?: string;
    statusCodes?: Record<string, number>;
  };
}

export function SaasPMSLogs() {
  const [logs, setLogs] = useState<PMSLog[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("3hours");
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [displayDateRange, setDisplayDateRange] =
    useState<string>("Last 3 hours");

  const [traceModalOpen, setTraceModalOpen] = useState(false);
  const [selectedTraceRequestId, setSelectedTraceRequestId] = useState<string | null>(null);
  const [traceData, setTraceData] = useState<TraceResponse | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState<string | null>(null);
  const [copiedTrace, setCopiedTrace] = useState(false);

  useEffect(() => {
    loadLogs();

    // Update the display text when dateRange changes
    if (dateRange.startsWith("custom_")) {
      const [_, startDateTime, endDateTime] = dateRange.split("_");
      const formattedStart = formatDateForDisplay(startDateTime);
      const formattedEnd = formatDateForDisplay(endDateTime);
      setDisplayDateRange(`Custom: ${formattedStart} - ${formattedEnd}`);
    } else {
      // Set display text for predefined ranges
      const rangeDisplayMap: Record<string, string> = {
        "30min": "Last 30 minutes",
        "60min": "Last 60 minutes",
        "3hours": "Last 3 hours",
        "6hours": "Last 6 hours",
        "12hours": "Last 12 hours",
        "24hours": "Last 24 hours",
        "3days": "Last 3 days",
        "7days": "Last 7 days",
        custom: "Custom date range",
      };
      setDisplayDateRange(rangeDisplayMap[dateRange] || dateRange);
    }
  }, [dateRange]);

  // Helper function to format date for display
  const formatDateForDisplay = (dateTimeString: string): string => {
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateTimeString;
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setError("User not authenticated");
        return;
      }

      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setError("No access token available");
        return;
      }

      // Use the authenticated user's ID as the saasId
      const saasId = user.id;

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

      // Build the URL based on whether it's a custom date range or predefined range
      let url = `${apiBaseUrl}/api/newrelic/logs/pms?saasId=${saasId}`;

      if (dateRange.startsWith("custom_")) {
        // Extract start and end dates from the custom format
        const [_, startDateTime, endDateTime] = dateRange.split("_");
        url += `&startDate=${encodeURIComponent(
          startDateTime
        )}&endDate=${encodeURIComponent(endDateTime)}`;
      } else if (dateRange === "custom") {
        // If "custom" is selected but no dates are submitted yet, use a default range
        // This shouldn't happen with our updated UI flow, but adding as a safeguard
        setError("Please select and apply a custom date range");
        return; // Exit the function to prevent the API call
      } else {
        url += `&dateRange=${dateRange}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      setLogs(data || []);
    } catch (err) {
      setError("Failed to load PMS logs");
      console.error("Error loading logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    if (value === "custom") {
      // If custom option is selected, either:
      // 1. Show date picker with empty dates if no custom range was previously set
      // 2. Show date picker with the current custom dates if a custom range was previously set
      if (dateRange.startsWith("custom_")) {
        const [_, startDateTime, endDateTime] = dateRange.split("_");
        setStartDate(startDateTime);
        setEndDate(endDateTime);
      }
      setShowCustomDatePicker(true);
      // Don't update dateRange yet - we'll do that when the user submits the custom dates
    } else {
      // For standard date ranges, update immediately and close the picker
      setDateRange(value);
      setShowCustomDatePicker(false);
    }
  };

  const handleCustomDateSubmit = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    // Clear any previous errors
    setError("");

    // Format: custom_YYYY-MM-DDTHH:MM:SS_YYYY-MM-DDTHH:MM:SS
    const formattedDateRange = `custom_${startDate}_${endDate}`;
    setDateRange(formattedDateRange);

    // Update display text immediately
    const formattedStart = formatDateForDisplay(startDate);
    const formattedEnd = formatDateForDisplay(endDate);
    setDisplayDateRange(`Custom: ${formattedStart} - ${formattedEnd}`);

    setShowCustomDatePicker(false);

    // Trigger logs reload with the new date range
    loadLogs();
  };

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

  const buildTraceUrl = (requestId: string, log?: PMSLog): string => {
    const base = `${apiBaseUrl}/v1/tracelog/trace/${encodeURIComponent(requestId)}`;
    const reqParams: Record<string, string> = {};
    if (log?.Time) {
      try {
        const dateStr = new Date(log.Time).toISOString().slice(0, 10).replace(/-/g, "");
        reqParams.date = dateStr;
      } catch {
        // ignore invalid date
      }
    }
    if (log?.Connector) reqParams.connector = log.Connector;
    if (log?.ConnectorHotelId) reqParams.hotelId = log.ConnectorHotelId;
    else if (log?.HotelCode) reqParams.hotelId = log.HotelCode;
    if (log?.Method) reqParams.method = log.Method;
    // Backend requires reqParams field on every request (even if empty)
    const params = new URLSearchParams();
    params.set("reqParams", JSON.stringify(reqParams));
    return `${base}?${params.toString()}`;
  };

  const fetchTrace = async (requestId: string, log?: PMSLog): Promise<TraceResponse> => {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !sessionData.session?.access_token) {
      throw new Error("Not authenticated");
    }
    const url = buildTraceUrl(requestId, log);
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try {
        const json = JSON.parse(text);
        if (json.message) message = json.message;
      } catch {
        // use raw text
      }
      if (response.status === 401) throw new Error("Unauthorized");
      if (response.status === 403) throw new Error("Access denied: You can only view your own trace files");
      if (response.status === 404) throw new Error(`Trace file not found for request ID: ${requestId}`);
      throw new Error(message || `Request failed with status ${response.status}`);
    }
    const data = await response.json();
    return data as TraceResponse;
  };

  const openTraceModal = async (log: PMSLog) => {
    if (!log.Ref) return;
    setSelectedTraceRequestId(log.Ref);
    setTraceModalOpen(true);
    setTraceData(null);
    setTraceError(null);
    setTraceLoading(true);
    try {
      const data = await fetchTrace(log.Ref, log);
      setTraceData(data);
    } catch (err) {
      setTraceError(err instanceof Error ? err.message : "Failed to load trace");
    } finally {
      setTraceLoading(false);
    }
  };

  const closeTraceModal = () => {
    setTraceModalOpen(false);
    setSelectedTraceRequestId(null);
    setTraceData(null);
    setTraceError(null);
    setCopiedTrace(false);
  };

  const copyTraceContent = async () => {
    if (!traceData) return;
    const text = JSON.stringify(traceData, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTrace(true);
      setTimeout(() => setCopiedTrace(false), 2000);
    } catch {
      setTraceError("Failed to copy to clipboard");
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading PMS logs...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <ScrollText className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-600">PMS Logs</h2>
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="dateRange" className="text-sm text-gray-600">
              Time Range:
            </label>
            <div className="relative">
              <div className="border border-gray-300 rounded px-2 py-1 text-sm bg-white flex items-center min-w-[180px]">
                <div
                  className="w-full cursor-pointer"
                  onClick={() => {
                    if (dateRange.startsWith("custom_")) {
                      // If custom date range is selected, open the date picker
                      const [_, startDateTime, endDateTime] =
                        dateRange.split("_");
                      setStartDate(startDateTime);
                      setEndDate(endDateTime);
                      setShowCustomDatePicker(true);
                    }
                  }}
                >
                  <select
                    id="dateRange"
                    value={
                      dateRange.startsWith("custom_") ? "custom" : dateRange
                    }
                    onChange={handleDateRangeChange}
                    className="w-full bg-transparent border-none focus:outline-none"
                  >
                    <option value="30min">Last 30 minutes</option>
                    <option value="60min">Last 60 minutes</option>
                    <option value="3hours">Last 3 hours</option>
                    <option value="6hours">Last 6 hours</option>
                    <option value="12hours">Last 12 hours</option>
                    <option value="24hours">Last 24 hours</option>
                    <option value="3days">Last 3 days</option>
                    <option value="7days">Last 7 days</option>
                    <option value="custom">
                      {dateRange.startsWith("custom_")
                        ? displayDateRange
                        : "Custom date range"}
                    </option>
                  </select>
                </div>
              </div>

              {showCustomDatePicker && (
                <div className="absolute mt-2 p-4 bg-white shadow-lg rounded border border-gray-200 z-10 left-0 top-full w-64">
                  <div className="flex flex-col space-y-3">
                    <div className="flex flex-col">
                      <label
                        htmlFor="startDate"
                        className="text-sm text-gray-600 mb-1"
                      >
                        Start Date:
                      </label>
                      <input
                        type="datetime-local"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="endDate"
                        className="text-sm text-gray-600 mb-1"
                      >
                        End Date:
                      </label>
                      <input
                        type="datetime-local"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setShowCustomDatePicker(false)}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCustomDateSubmit}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => loadLogs()}
              className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Time</th>
                <th className="table-header">Hotel Code</th>
                <th className="table-header">Connector</th>
                <th className="table-header">Method</th>
                <th className="table-header w-48 max-w-48">Action</th>
                <th className="table-header">Response Time</th>
                <th className="table-header">Size</th>
                <th className="table-header">Datapoints</th>
                <th className="table-header">Status</th>
                <th className="table-header">Trace</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {logs.map((log, index) => (
                <tr key={log.Ref ?? `log-${index}`} className="table-row">
                  <td className="table-cell">
                    {new Date(log.Time).toLocaleString()}
                  </td>
                  <td className="table-cell">{log.HotelCode}</td>
                  <td className="table-cell">{log.Connector}</td>
                  <td className="table-cell">
                    <span
                      className={`status-badge ${
                        log.Method === "GET"
                          ? "bg-blue-100"
                          : log.Method === "POST"
                          ? "bg-green-100"
                          : log.Method === "PUT"
                          ? "bg-yellow-100"
                          : "bg-red-100"
                      }`}
                    >
                      {log.Method}
                    </span>
                  </td>
                  <td
                    className="table-cell w-48 max-w-48"
                    title={log.Action}
                  >
                    <span className="block truncate">
                      {log.Action}
                    </span>
                  </td>
                  <td className="table-cell">{log.ResponseTime}</td>
                  <td className="table-cell">{log.SizeBytes}</td>
                  <td className="table-cell">{log.Datapoints}</td>
                  <td className="table-cell">
                    <span
                      className={`status-badge ${
                        log.StatusCode >= "200" && log.StatusCode < "300"
                          ? "status-active"
                          : "status-error"
                      }`}
                    >
                      {log.StatusCode} - {log.Status}
                    </span>
                  </td>
                  <td className="table-cell">
                    {log.Ref ? (
                      <button
                        type="button"
                        onClick={() => openTraceModal(log)}
                        className="text-blue-500 hover:text-blue-700 flex items-center bg-transparent border-none cursor-pointer p-0 font-inherit"
                      >
                        <ScrollText className="h-4 w-4 mr-1" />
                        Trace
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {logs.length === 0 && !error && (
            <div className="text-center py-8">
              <p className="px-6 py-4 text-center text-sm text-gray-500">
                No logs found. Logs will appear here when you make API calls to
                hotel PMS systems.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Trace modal */}
      {traceModalOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeTraceModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="trace-modal-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 id="trace-modal-title" className="text-lg font-semibold text-gray-800">
                Trace: {selectedTraceRequestId ?? ""}
              </h3>
              <div className="flex items-center gap-2">
                {traceData && (
                  <button
                    type="button"
                    onClick={copyTraceContent}
                    className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
                  >
                    {copiedTrace ? (
                      <>
                        <Check className="h-4 w-4 text-green-600" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeTraceModal}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-4 py-3 flex-1 min-h-0 overflow-hidden flex flex-col">
              {traceLoading && (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  Loading trace...
                </div>
              )}
              {traceError && !traceLoading && (
                <div className="py-4 text-red-600 text-sm">
                  {traceError}
                </div>
              )}
              {traceData && !traceLoading && (
                <pre className="flex-1 overflow-y-auto text-xs text-gray-800 bg-gray-50 rounded p-4 border border-gray-200 max-h-[80vh]">
                  <code>{JSON.stringify(traceData, null, 2)}</code>
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
